import {inject, Injectable} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import {catchError, filter, map, Observable, of, take, tap} from 'rxjs';
import {SessionState} from './shared/enum/session-state.enum';
import {SessionService} from './shared/services/session.service';
import {AuthService} from './shared/services/auth.service';
import {HOME, WELCOME} from './shared/constants';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate, CanActivateChild {
  // INJECT
  private authService = inject(AuthService);
  private sessionService = inject(SessionService);
  private router = inject(Router);

  canEnter(requestedUrl: string): Observable<boolean> {
    return this.sessionService.sessionStates$.pipe(
      filter((states) => states.length > 0),
      take(1),
      map(
        (states) =>
          states.includes(SessionState.CURRENTUSER) ||
          states.includes(SessionState.IMPERSONATESESSION),
      ),
      tap((isAllowed) => {
        if (!isAllowed) {
          this.loginWithRedirect(requestedUrl);
        }
      }),
      catchError(() => {
        this.loginWithRedirect(requestedUrl);
        return of(false);
      }),
    );
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean | Observable<boolean> | Promise<boolean> {
    return this.canEnter(state.url);
  }

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean | Observable<boolean> | Promise<boolean> {
    return this.canEnter(state.url);
  }

  private loginWithRedirect(requestedUrl: string) {
    // If the route is valid (not wildcard) and not HOME or WELCOME, redirect to login with redirect_uri
    if (requestedUrl && requestedUrl !== WELCOME && requestedUrl !== HOME) {
      // A refused sign-in has to land somewhere that explains itself, otherwise
      // the rejection is an unhandled error and the user sees nothing.
      this.authService.login(requestedUrl).subscribe({
        error: () => this.router.navigate([WELCOME]),
      });
    } else {
      this.router.navigate([WELCOME]);
    }
  }
}
